import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import Card from '../components/UI/Card';
import golkaLogo from '../assets/golka-logo.png';

const STORAGE_ATTEMPTS = 'login_attempts';
const STORAGE_COOLDOWN_UNTIL = 'login_cooldown_until';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [attempts, setAttempts] = useState(() => {
    const saved = localStorage.getItem(STORAGE_ATTEMPTS);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [cooldownRemaining, setCooldownRemaining] = useState(() => {
    const until = localStorage.getItem(STORAGE_COOLDOWN_UNTIL);
    if (until) {
      const remaining = Math.max(0, Math.floor((parseInt(until, 10) - Date.now()) / 1000));
      return remaining;
    }
    return 0;
  });

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(STORAGE_ATTEMPTS, attempts.toString());
  }, [attempts]);

  useEffect(() => {
    if (cooldownRemaining > 0) {
      const expiry = Date.now() + cooldownRemaining * 1000;
      localStorage.setItem(STORAGE_COOLDOWN_UNTIL, expiry.toString());
    } else {
      localStorage.removeItem(STORAGE_COOLDOWN_UNTIL);
    }
  }, [cooldownRemaining]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setTimeout(() => setCooldownRemaining(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownRemaining]);

  useEffect(() => {
    if (cooldownRemaining === 0 && attempts >= 3) {
      setAttempts(0);
      setGeneralError('');
      localStorage.removeItem(STORAGE_ATTEMPTS);
    }
  }, [cooldownRemaining, attempts]);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email обовʼязковий';
    if (!email.includes('@')) newErrors.email = 'Невірний формат email';
    if (!password) newErrors.password = 'Пароль обовʼязковий';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    setGeneralError('');
    if (!validateForm()) return;
    if (cooldownRemaining > 0) return;

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      setAttempts(0);
      setCooldownRemaining(0);
      localStorage.removeItem(STORAGE_ATTEMPTS);
      localStorage.removeItem(STORAGE_COOLDOWN_UNTIL);
      navigate('/profile');
    } else {
      setPassword('');
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      // Keep the reason Keycloak gave (unfinished registration, disabled
      // account) instead of always blaming the password.
      setGeneralError(result.message || 'Невірний email або пароль. Спробуйте ще раз.');
      if (newAttempts >= 3) {
        setCooldownRemaining(60);
        setGeneralError('Забагато невдалих спроб. Зачекайте 60 секунд.');
      }
    }
  };

  const isButtonDisabled = loading || cooldownRemaining > 0;
  const buttonText = loading
    ? 'Вхід...'
    : cooldownRemaining > 0
      ? `Зачекайте ${cooldownRemaining}с`
      : 'Увійти';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="mb-4">
          <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            На головну
          </Link>
        </div>
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src={golkaLogo} alt="Голка" className="h-10 w-auto mx-auto" />
          </Link>
        </div>
        <Card>
          <div className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Вхід</h2>
              <p className="text-gray-600 mt-2">Увійдіть для доступу до API</p>
            </div>
            {generalError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {generalError}
              </div>
            )}
            <div>
              <Input
                label="Email"
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                required
                placeholder="your@email.com"
                disabled={cooldownRemaining > 0}
              />
              <Input
                label="Пароль"
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                required
                placeholder="••••••••"
                disabled={cooldownRemaining > 0}
              />
              <div className="-mt-2 mb-4 text-right">
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  Забули пароль?
                </Link>
              </div>
              {cooldownRemaining > 0 && (
                <div className="bg-yellow-50 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                  ⏱ Зачекайте {cooldownRemaining} секунд.
                </div>
              )}
              <Button
                onClick={handleLogin}
                variant="primary"
                size="lg"
                className="w-full mt-6"
                disabled={isButtonDisabled}
              >
                {buttonText}
              </Button>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Немає акаунту?{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  Зареєструватися
                </Link>
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;